/**
 * AdoptionTable — which consumers adopted which catalog version, with upgrade and revert. Demo page: /tables/basic-tables.
 */
import { GitBranch } from 'lucide-react';
import { Badge, Button, EmptyState } from '../../../components/ui/index.js';
import { Card } from '../../../components/tailgrids/core/card.js';
import type { CatalogAdoptionStatus } from './catalog-types.js';
import { formatCatalogAdoptionLabel } from './catalog-helpers.js';

export function AdoptionTable({
  adoptions,
  onUpgrade,
  onRevert,
  upgradingKey,
  revertingKey,
}: {
  adoptions: CatalogAdoptionStatus[];
  onUpgrade: (consumerKey: string) => void;
  onRevert: (consumerKey: string) => void;
  upgradingKey?: string | null;
  revertingKey?: string | null;
}) {
  if (adoptions.length === 0) {
    return (
      <EmptyState
        icon={GitBranch}
        title="No catalog adoptions"
        description="Promoted library items have not been adopted by workflows, workforce, or marketplace yet."
      />
    );
  }
  return (
    <Card className="divide-y overflow-hidden p-0">
      {adoptions.map((adoption) => (
        <div
          key={adoption.consumerKey}
          className="p-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3"
        >
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-semibold text-heading">{adoption.label}</h2>
              <Badge
                variant={
                  adoption.status === 'stale' ? 'warning' : adoption.status === 'current' ? 'success' : 'neutral'
                }
              >
                {adoption.status}
              </Badge>
              <Badge variant="neutral">{formatCatalogAdoptionLabel(adoption.consumerType)}</Badge>
            </div>
            <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted">
              <span>{formatCatalogAdoptionLabel(adoption.targetType)}</span>
              <span>adopted v{adoption.adoptedVersion ?? 'n/a'}</span>
              <span>latest v{adoption.latestVersion ?? 'n/a'}</span>
              {adoption.previousVersion && <span>previous v{adoption.previousVersion}</span>}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              className={
                adoption.status === 'stale' ? 'bg-warning-muted text-warning hover:bg-warning-muted/80' : undefined
              }
              disabled={!adoption.canUpgrade}
              loading={upgradingKey === adoption.consumerKey}
              onClick={() => onUpgrade(adoption.consumerKey)}
            >
              Upgrade
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={!adoption.canRevert}
              loading={revertingKey === adoption.consumerKey}
              onClick={() => onRevert(adoption.consumerKey)}
            >
              Revert
            </Button>
          </div>
        </div>
      ))}
    </Card>
  );
}
